/**
 * @file dcp/src/dcp-client/worker/supervisor2/module-cache.js
 *
 * A cache for modules.
 *
 * @author Matthew Palma, mpalma@kingsds.network
 *         Paul, paul@distributive.network
 * @date May 2019
 *       June 2022, April 2023
 * @module module-cache
 * @copyright Copyright (c) 2018-2025, Distributive Corp. All Rights Reserved
 */
// @ts-check
'use strict';

const debugging = require('dcp/debugging').scope('worker');
const { reallyJustFetch } = require('dcp/utils');
const utils = require('dcp/utils');

/** @typedef {import('./index').Supervisor} Supervisor */

class ModuleCache
{
  /**
   * @constructor
   * @param {Supervisor} supervisor
   */
  constructor (supervisor)
  {
    /** @type {Supervisor} */
    this.supervisor = supervisor;
    this.cache = {};  // Modules keyed by module group.
  }

  /**
   * Returns an object listing what jobs and modules are currently cached.
   * @returns {string} - in the form: moduleGroup1: [ job1 ], moduleGroup2: [ job1, job2 ]...
   */
  get cacheDescription ()
  {
    const desc = {};
    for (const [ id, moduleJobs ] of Object.entries(this.cache))
      desc[id] = moduleJobs.jobs;
    return JSON.stringify(desc);
  }

  /**
   * Attempts to look up an item from the cache.
   * @param {string} id  - the items module group name
   * @param {string} job - the job calling fetch
   * @returns {any} The value of the stored item or undefined if nothing is found
   */
  fetch(id, job)
  {
    const moduleJobs = this.cache[id];
    if (!moduleJobs) return moduleJobs;
    if (moduleJobs.jobs.indexOf(job) === -1 )
      moduleJobs.jobs.push(job);
    return moduleJobs.module;
  }

  /**
   * Stores a fetched value for one of the caches.
   * @param {string} id  - the items identifier module group name
   * @param {string} job - the job calling fetch
   * @param {any} value  - the item to store
   * @returns {any} - @value passed in
   */
  store (id, job, value)
  {
    if (!this.cache[id]) this.cache[id] = { module: value, jobs: [ job ] };
    else if (this.cache[id].jobs.indexOf(job) === -1) this.cache[id].jobs.push(job);
    return value
  }
    
  /**
   * Removes a job reference from a module cache.
   * When there are no jobs left, the module cache entry is destroyed.
   * @param {string} job - the job to be removed.
   */
  removeJob (job)
  {
    for (const [ id, moduleJobs ] of Object.entries(this.cache))
    {
      moduleJobs.jobs = moduleJobs.jobs.filter(_job => _job !== job);
      if (moduleJobs.jobs.length < 1) delete this.cache[id];
    }
  }

  /**
   * Attempts to fetch a module group from the cache and
   * if it's not found it attempts to fetch then store
   * the module group from the package manager.
   *
   * @param {array} modulesArray - the array of modules requested 
   *        - (when stringified it's the identifier of the module group)
   *
   * @returns {Promise<object>} - the module group
   * @throws when the module group can not be fetched
   */
  async fetchModule(modulesArray, job)
  {
    const cacheKey = JSON.stringify(modulesArray);
    let modules = this.fetch(cacheKey, job);
    if (modules)
    {
      debugging() && console.debug('FetchModule: Cache hit', { cacheKey, job });
      return modules;
    }

    debugging() && console.debug('FetchModule for modules name(s)', cacheKey);

    const resp = await this.supervisor.dcp4.packageManager.request('fetchModuleURL', { modules: modulesArray });

    if (!resp.success)
    {
      const errorMsg = `FetchModule: Calling the package manager failed for module name(s) ${cacheKey}`;
      if (resp.payload)
      {
        debugging() && console.debug(errorMsg, resp.payload);
        throw resp.payload;
      }
      debugging() && console.debug(errorMsg);
      throw new Error(errorMsg);
    }

    modules = resp.payload;

    if (modules?.code === 'ENOENT') /* non-existent module */
      throw modules;

    for (const moduleGroup of Object.values(modules))
    {
      for (const moduleName in moduleGroup)
      {
        // Treat any error from fetching data as transient & willing to retry.
        // Return null to inform the sandbox to terminate itself, but be willing to
        // try new slices later. If multiple slices try to fetch modules in short order,
        // add delay before allowing a new attempt
        const url = new URL(moduleGroup[moduleName]);
        const urlSegment = url.origin + '/' + url.pathname.split('/')[1];
        const delay = this.supervisor.fetchBackoffs[urlSegment];
        if (delay && delay.lastErrorTime + delay.wait*1000 > Date.now())
          return null;

        let fetchResultXHR;
        try
        {
          fetchResultXHR = await reallyJustFetch(moduleGroup[moduleName]);
          if (delay)
            delete this.supervisor.fetchBackoffs[urlSegment];
        }
        catch (_error)
        {
          if (delay)
          {
            delay.lastErrorTime = Date.now()
            delay.wait = delay.backoff.next();
            return null;
          }
          const backoff = new utils.Backoff({ mult: 2, base: 2, max: 180 });
          this.supervisor.fetchBackoffs[urlSegment] = {
            lastErrorTime: Date.now(),
            wait: backoff.next(),
            backoff,
          };
          return null;
        }
        moduleGroup[moduleName] = fetchResultXHR.responseText;
      }
    }
    
    return this.store(cacheKey, job, modules);
  }
}
exports.ModuleCache = ModuleCache;
